"use client";

import { useSignIn, useUser } from "@klarify/auth/client";
import { EMAIL_QUERY_PARAM } from "@klarify/constants";
import { useSearchParams } from "@klarify/i18n/navigation";
import { getLocalizedPathname } from "@klarify/i18n/path";
import { logger } from "@klarify/telemetry";
import { isDeadTicketError } from "@klarify/ui/components/auth/clerk-errors";
import { CustomSignIn } from "@klarify/ui/components/auth/custom-signin";
import { CustomSignUp } from "@klarify/ui/components/auth/custom-signup";
import {
	SignupTestimonialDesktop,
	SignupTestimonialMobileMini,
	SignupWelcomeMobile,
} from "@klarify/ui/components/auth/signup-testimonial";
import { Spinner } from "@klarify/ui/components/spinner";
import { Alert } from "@klarify/ui/components/ui/alert";
import { Tabs, TabsList, TabsTrigger } from "@klarify/ui/components/ui/tabs";
import { useAuthRedirects } from "@klarify/ui/hooks/auth-redirects";
import { useMediaQuery } from "@klarify/ui/hooks/media-query";
import { cn } from "@klarify/ui/lib/utils";
import { Trans, useLingui } from "@lingui/react/macro";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type Role = "Client" | "Therapist";
type AuthMode = "login" | "register";
// "expired" is only reached when Clerk refused the ticket itself, so a transport
// failure never tells the user their invitation is gone.
type TicketFallback = "none" | "expired" | "failed";

interface AuthPageProps {
	mode: AuthMode;
	role: Role;
	showTabs?: boolean;
}

export function ClientAuthPage({
	mode,
	role,
	showTabs: shouldShowTabs,
}: AuthPageProps) {
	const { t } = useLingui();
	const auth = useUser();
	const [isPendingVerification, setIsPendingVerification] = useState(false);
	const [isRedirecting, setIsRedirecting] = useState(false);
	const [isTwoFactor, setIsTwoFactor] = useState(false);
	const [mobileShowForm, setMobileShowForm] = useState(false);
	const [currentRole, setCurrentRole] = useState<Role>(role);

	const searchParams = useSearchParams();

	const {
		goto,
		withParams,
		isRedirecting: isAuthRedirecting,
	} = useAuthRedirects();
	const redirectTo = useMemo(() => {
		return withParams("redirect");
	}, [withParams]);

	const isSignedIn = useMemo(() => {
		return auth.isLoaded && auth.isSignedIn;
	}, [auth]);

	const invitationTicket = searchParams.get("__clerk_ticket");
	const invitationStatus = searchParams.get("__clerk_status");
	const hasInvitation = Boolean(invitationTicket && invitationStatus);
	// `hasInvitation` stays true even after a dead ticket, so form choice stays
	// driven by `__clerk_status` rather than the URL's mode (always register).
	const [ticketFallback, setTicketFallback] = useState<TicketFallback>("none");
	const ticketRejected = ticketFallback !== "none";

	// Handle invitation sign-in/complete statuses (existing users)
	const { signIn, setActive } = useSignIn();
	useEffect(() => {
		if (!(hasInvitation && invitationTicket)) {
			return;
		}

		if (invitationStatus === "complete") {
			setIsRedirecting(true);
			window.location.href = redirectTo;
			return;
		}

		if (invitationStatus === "sign_in" && signIn && setActive) {
			setIsRedirecting(true);
			void signIn
				.create({ strategy: "ticket", ticket: invitationTicket })
				.then(async (result) => {
					if (result.status === "complete") {
						await setActive({ session: result.createdSessionId });
						window.location.href = redirectTo;
						return;
					}

					// Accepted but needs another step (e.g. needs_second_factor for MFA).
					// Hand off to CustomSignIn rather than duplicating its 2FA UI here.
					// Never "expired" — the ticket was honoured.
					setTicketFallback("failed");
					setIsRedirecting(false);
				})
				.catch((err: unknown) => {
					console.error("Invitation sign-in failed:", err);
					// Clerk's 30-day ticket expiry outlives the clinic's open invitation;
					// fall back to sign-in — the invite is still waiting in the clinic switcher.
					setTicketFallback(isDeadTicketError(err) ? "expired" : "failed");
					setIsRedirecting(false);
				});
		}
	}, [
		hasInvitation,
		invitationTicket,
		invitationStatus,
		signIn,
		setActive,
		redirectTo,
	]);

	// Only redirect to /auth/redirect if the user was already signed in when
	// they landed on this page (e.g. deep-linking /auth/therapist/login while
	// having an active session). When the user just signed in/up via the forms
	// on *this* page, the form itself handles the redirect via
	// window.location.href; firing a second `goto` here would cause a duplicate
	// request to /auth/redirect and trigger double clinic creation.
	if (
		isSignedIn &&
		!isRedirecting &&
		!isPendingVerification &&
		!isAuthRedirecting &&
		!hasInvitation
	) {
		goto("redirect");
	}

	// No `auth.isLoaded` gate: the tabs, mobile mini, and forms must paint from
	// SSR. When Clerk resolves a signed-in user, `!auth.isSignedIn` hides them and
	// the redirect above fires.
	const showFormChrome = useMemo(() => {
		return (
			!(
				auth.isSignedIn ||
				isPendingVerification ||
				isRedirecting ||
				isTwoFactor
			) &&
			(!hasInvitation || ticketRejected)
		);
	}, [
		auth,
		isPendingVerification,
		isRedirecting,
		isTwoFactor,
		hasInvitation,
		ticketRejected,
	]);

	const showTabs = showFormChrome && shouldShowTabs;

	// An existing user with a dead ticket can't sign *up* with the invited address,
	// so hand them the sign-in form even though the URL always lands on register.
	const showSignInFallback =
		hasInvitation && invitationStatus === "sign_in" && ticketRejected;
	const showSignUpForm =
		!showSignInFallback &&
		(mode === "register" || (hasInvitation && invitationStatus === "sign_up"));
	// Catch-all for a login URL: an unrecognised `__clerk_status` must still
	// render a form, never nothing.
	const showSignInForm =
		showSignInFallback || (mode === "login" && !showSignUpForm);
	// Transient by construction: every outcome of the ticket call either
	// navigates away or clears `isRedirecting`.
	const isExchangingTicket = hasInvitation && isRedirecting && !ticketRejected;

	const expectedEmail = useMemo(() => {
		return searchParams.get(EMAIL_QUERY_PARAM);
	}, [searchParams]);

	// Holds the in-flight set-time/set-role writes. Submit paths await this before
	// calling Clerk so /auth/redirect reads the role + is-new-user cookies. The form
	// no longer unmounts while these run, so typed input survives a role-tab switch.
	const cookiePromiseRef = useRef<Promise<void>>(undefined);

	const applyCookies = useCallback(() => {
		const promise = Promise.all([
			fetch("/api/auth/set-time"),
			fetch(`/api/auth/set-role/${currentRole}`),
		])
			.then(() => undefined)
			.catch((error: unknown) => {
				logger.error(error);
			});
		cookiePromiseRef.current = promise;
		return promise;
	}, [currentRole]);

	useEffect(() => {
		void applyCookies();
	}, [applyCookies]);

	const awaitCookies = useCallback(async () => {
		await cookiePromiseRef.current;
	}, []);

	const loginRedirect = useMemo(() => {
		if (currentRole === "Client") {
			return withParams("clientLogin");
		}
		return withParams("therapistLogin");
	}, [withParams, currentRole]);

	const registerRedirect = useMemo(() => {
		if (currentRole === "Client") {
			return withParams("clientRegister");
		}
		return withParams("therapistRegister");
	}, [withParams, currentRole]);

	const handleTabChange = (value: string) => {
		const newRole = value as Role;
		if (newRole === currentRole) {
			return;
		}
		setCurrentRole(newRole);

		// Update URL without remounting the page (preserves welcome/form state,
		// typed form inputs, etc.). Next's router state will resync on next
		// real navigation.
		if (typeof window !== "undefined") {
			const roleSegment = newRole === "Client" ? "client" : "therapist";
			const modeSegment = mode === "login" ? "login" : "register";
			const params = searchParams.toString();
			const nextPath = getLocalizedPathname(
				`/auth/${roleSegment}/${modeSegment}`,
			);
			window.history.replaceState(
				null,
				"",
				params ? `${nextPath}?${params}` : nextPath,
			);
		}
	};

	const ssoCallbackUrl = useMemo(() => {
		return withParams("ssoCallback");
	}, [withParams]);

	const continueWithGoogle = useCallback(async () => {
		if (!signIn) {
			return;
		}
		setIsRedirecting(true);
		// Cookies drive /auth/redirect role + is-new-user routing; they must land
		// before the OAuth navigation leaves the page. A fresh run (not just
		// awaiting the mount write) also rewrites the redirect-time cookie if its
		// 1h maxAge lapsed while the tab idled.
		await applyCookies();
		signIn
			.authenticateWithRedirect({
				strategy: "oauth_google",
				redirectUrl: ssoCallbackUrl,
				redirectUrlComplete: redirectTo,
				continueSignUp: true,
			})
			.catch((err) => {
				console.error("Google OAuth failed:", err);
				setIsRedirecting(false);
			});
	}, [signIn, redirectTo, ssoCallbackUrl, applyCookies]);

	const { isMobile, isTablet, isDesktop } = useMediaQuery();

	// Paints from SSR (no auth.isLoaded gate) so mobile register shows the
	// welcome screen first, not the form it covers; a signed-in user unmounts it
	// when Clerk resolves and is redirected anyway. `isRedirecting` is deliberately
	// not consulted: the Google path must keep the overlay (buttons disabled via
	// `busy`) over the form until the OAuth navigation leaves the page. Never on
	// an invitation URL: after a dead ticket degrades to a normal sign-up, the
	// welcome screen would hide the form mid-flow.
	const showWelcomeMobile =
		!(auth.isSignedIn || isPendingVerification) &&
		mode === "register" &&
		!mobileShowForm &&
		!expectedEmail &&
		!hasInvitation &&
		!isDesktop;

	return (
		<div className="grid h-dvh w-full grid-cols-1 lg:grid-cols-[1.05fr_1fr]">
			<SignupTestimonialDesktop className="hidden lg:flex" />

			{showWelcomeMobile && (
				<SignupWelcomeMobile
					className="lg:hidden"
					onCreateAccount={() => setMobileShowForm(true)}
					onContinueWithGoogle={continueWithGoogle}
					signInUrl={loginRedirect}
					busy={isRedirecting}
					googleDisabled={!signIn}
				/>
			)}

			<section
				className="overflow-y-auto bg-klarify-cloud-50"
				// `isMobile || isTablet`, not `!isDesktop`: all three are false in the
				// server snapshot, so SSR HTML never marks the section inert and a
				// desktop visitor can use the form during hydration. Mobile and tablet
				// apply inert on hydration, once the overlay actually covers the form.
				inert={showWelcomeMobile && (isMobile || isTablet)}
			>
				<div className="grid min-h-full place-items-center px-5 py-8 lg:px-8 lg:py-10">
					<div
						className={cn(
							"flex w-full max-w-110 flex-col justify-center gap-4",
							// Reserves register card space so the column cannot collapse.
							// Shorter content (verify, 2FA, spinners) centers inside it.
							mode === "register" ? "min-h-[600px]" : "min-h-[460px]",
						)}
						data-dd-privacy="mask"
					>
						{showFormChrome && mode === "login" && (
							<SignupTestimonialMobileMini className="lg:hidden" />
						)}

						{showTabs && (
							<Tabs
								className="w-full"
								defaultValue="Therapist"
								value={currentRole}
								onValueChange={handleTabChange}
							>
								<TabsList
									className="w-full cursor-pointer"
									aria-label={t`Manage your account`}
									data-dd-privacy="allow"
								>
									<TabsTrigger
										className="w-1/2"
										value="Therapist"
									>
										<Trans>Therapist</Trans>
									</TabsTrigger>

									<TabsTrigger
										className="w-1/2"
										value="Client"
									>
										<Trans>Client</Trans>
									</TabsTrigger>
								</TabsList>
							</Tabs>
						)}

						{showSignInFallback && (
							<Alert className="border-klarify-gray-mod-200 bg-klarify-gray-mod-50 text-klarify-gray-mod-700">
								{ticketFallback === "expired" ? (
									<Trans>
										This invitation link has expired. Sign in with the email
										address it was sent to and we'll add you to the clinic.
									</Trans>
								) : (
									<Trans>
										We couldn't open your invitation link. Sign in with the
										email address it was sent to and we'll add you to the
										clinic.
									</Trans>
								)}
							</Alert>
						)}

						{isExchangingTicket && (
							<div className="flex w-full justify-center">
								<Spinner />
							</div>
						)}

						{!isExchangingTicket && (
							<>
								{showSignInForm && (
									<CustomSignIn
										forceRedirectUrl={redirectTo}
										redirectUrl={redirectTo}
										signUpUrl={registerRedirect}
										initialEmail={expectedEmail ?? undefined}
										onRedirectStart={() => setIsRedirecting(true)}
										onTwoFactorStateChange={setIsTwoFactor}
										awaitCookies={awaitCookies}
										refreshCookies={applyCookies}
									/>
								)}
								{showSignUpForm && (
									<CustomSignUp
										forceRedirectUrl={redirectTo}
										redirectUrl={redirectTo}
										signInUrl={loginRedirect}
										initialEmail={expectedEmail ?? undefined}
										ticket={invitationTicket ?? undefined}
										onTicketRejected={() => setTicketFallback("expired")}
										onVerificationStateChange={setIsPendingVerification}
										awaitCookies={awaitCookies}
										refreshCookies={applyCookies}
									/>
								)}
							</>
						)}
					</div>
				</div>
			</section>
		</div>
	);
}
